import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../../i18n.ts';

export interface CookiePreferences {
  necessary: boolean; // Always true — auth, CSRF, language
  analytics: boolean; // Future: usage analytics
  timestamp: string; // When consent was given
}

const CONSENT_KEY = 'pt_cookie_consent';

export const getStoredConsent = (): CookiePreferences | null => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/** Event dat afgaat zodra de gebruiker de cookiebanner heeft beantwoord. */
export const CONSENT_EVENT = 'pt:cookie-consent';

/**
 * Heeft de gebruiker de cookiebanner beantwoord?
 *
 * Poort voor alles wat een derde partij aanroept — nu het Google Maps-widget op
 * het dashboard. De Maps-SDK haalt zelf lettertypes op bij Google, dus het laden
 * ervan stuurt het IP-adres van de bezoeker naar Google. Dat mag pas nadat de
 * banner is beantwoord; daarvoor toont het dashboard de plaatsenlijst die er
 * toch al als terugval in zat.
 */
export const hasAnsweredCookieBanner = (): boolean => getStoredConsent() !== null;

/**
 * Leg de keuze over gebruiksstatistieken vast — lokaal én bij het account.
 *
 * Staat op module-niveau omdat de banner niet de enige plek mag zijn waar dit
 * kan. Artikel 7 lid 3 AVG eist dat intrekken net zo eenvoudig is als geven, en
 * de banner verschijnt maar één keer: daarna was de keuze onbereikbaar. De
 * privacy-instellingen roepen dezelfde functie aan, zodat er één plek is die
 * weet hoe een toestemming wordt weggeschreven.
 */
export const persistConsent = (analytics: boolean): CookiePreferences => {
  const preferences: CookiePreferences = {
    necessary: true,
    analytics,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
  } catch {
    // Privémodus of geblokkeerde opslag — de server-sync hieronder blijft de
    // bron van waarheid voor een ingelogde gebruiker.
  }
  // Zodat schermen die op toestemming wachten (dashboard-kaart) direct bijwerken
  // zonder dat de gebruiker eerst moet navigeren.
  window.dispatchEvent(new Event(CONSENT_EVENT));

  // Sync to server if user is authenticated
  fetch('/api/account/consent', {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-csrf-token': document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/)?.[1] || '',
    },
    body: JSON.stringify({ preferences }),
  }).catch(() => {}); // Best-effort sync

  return preferences;
};

export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Don't overlay the cookie banner on the tree/canvas view
  const onTreeCanvas = location.pathname.startsWith('/tree');

  useEffect(() => {
    if (onTreeCanvas) return;
    const consent = getStoredConsent();
    if (!consent) {
      // Small delay so banner doesn't flash on page load
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [onTreeCanvas]);

  const saveConsent = (analytics: boolean) => {
    persistConsent(analytics);
    setVisible(false);
  };

  if (!visible || onTreeCanvas) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[50] pointer-events-none animate-in slide-in-from-bottom duration-500">
      <div className="max-w-3xl mx-auto p-4">
        <div className="pointer-events-auto bg-white dark:bg-[var(--bg-widget)] rounded-2xl shadow-2xl border border-pt-forest/10 dark:border-white/10 overflow-hidden">
          {/* Header */}
          <div className="flex items-start gap-3 p-5 pb-3">
            <div className="w-10 h-10 rounded-full bg-pt-gold/20 flex items-center justify-center shrink-0 mt-0.5">
              <Cookie size={20} className="text-pt-forest dark:text-pt-gold" />
            </div>
            <div className="flex-1">
              <h3 className="font-black text-pt-forest dark:text-[var(--text-main)] text-sm">
                {t('cookieTitle')}
              </h3>
              <p className="text-pt-sage-text dark:text-[var(--text-muted)] text-xs mt-1 leading-relaxed">
                {t('cookieDescription')}
              </p>
            </div>
          </div>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 px-5 py-1 text-[10px] font-bold uppercase tracking-wider text-pt-forest/50 dark:text-white/40 hover:text-pt-forest dark:hover:text-white/80 transition-colors"
          >
            {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {t('cookieDetails')}
          </button>

          {/* Cookie categories */}
          {showDetails && (
            <div className="px-5 pb-3 space-y-3 animate-in fade-in duration-200">
              {/* Necessary */}
              <div className="flex items-center justify-between p-3 bg-pt-cream/60 dark:bg-white/5 rounded-xl">
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-pt-forest dark:text-[var(--text-muted)]" />
                  <div>
                    <span className="text-xs font-bold text-pt-forest dark:text-[var(--text-main)]">
                      {t('cookieNecessary')}
                    </span>
                    <p className="text-[10px] text-pt-sage-text dark:text-[var(--text-muted)]">
                      {t('cookieNecessaryDesc')}
                    </p>
                  </div>
                </div>
                <span className="text-[9px] font-black uppercase tracking-wider text-pt-forest/40 dark:text-white/30">
                  {t('cookieAlwaysOn')}
                </span>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-3 bg-pt-cream/60 dark:bg-white/5 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="w-3.5 h-3.5 rounded-sm border border-pt-forest/30 dark:border-white/30" />
                  <div>
                    <span className="text-xs font-bold text-pt-forest dark:text-[var(--text-main)]">
                      {t('cookieAnalytics')}
                    </span>
                    <p className="text-[10px] text-pt-sage-text dark:text-[var(--text-muted)]">
                      {t('cookieAnalyticsDesc')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                  className={`w-8 h-4 rounded-full transition-colors relative shrink-0 ${analyticsEnabled ? 'bg-pt-forest dark:bg-pt-gold' : 'bg-pt-forest/20 dark:bg-white/15'}`}
                >
                  <span
                    className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${analyticsEnabled ? 'left-4' : 'left-0.5'}`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 p-4 pt-2 border-t border-pt-forest/5 dark:border-white/8">
            <button
              onClick={() => saveConsent(false)}
              className="flex-1 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-pt-forest/60 dark:text-white/50 hover:text-pt-forest dark:hover:text-white border border-pt-forest/20 dark:border-white/20 rounded-full transition-colors"
            >
              {t('cookieNecessaryOnly')}
            </button>
            <button
              onClick={() => saveConsent(true)}
              className="flex-[2] px-4 py-2.5 text-[10px] font-black uppercase tracking-wider bg-pt-gold text-pt-forest rounded-full hover:bg-pt-gold/90 transition-colors shadow-sm"
            >
              {t('cookieAcceptAll')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CONSENT_KEY };
